import React, {
  type Dispatch,
  type SetStateAction,
  useMemo,
  useRef,
} from "react";
import AppetizeSimulator from "@/components/simulator";
import {
  type BuildData,
  type CompanyData,
  type SessionConfigData,
  type Test,
} from "@/utils/types";
import { appetizeSessionStore } from "@/atoms";
import { useInteractions } from "@/components/device-provider/use-interactions-hook";
import { AnnotatedScreenshot } from "@/routes/sessions/AnnotatedScreenshot";
import { type DeviceContextState } from "@/routes/test-builder-page";
import styled from "@emotion/styled";
import { useStore } from "@tanstack/react-store";

const AppetizeDeviceProvider = ({
  buildData,
  companyData,
  sessionConfig,
  test,
  deviceContextState,
  setDeviceContextState,
}: {
  buildData: BuildData;
  companyData: CompanyData;
  sessionConfig?: SessionConfigData;
  test: Test;
  deviceContextState?: DeviceContextState;
  setDeviceContextState: Dispatch<SetStateAction<DeviceContextState>>;
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { cursorPos, isTapping } = useInteractions({
    wrapperRef,
    companyData,
    platform: sessionConfig?.platform.toLowerCase(),
    deviceContextState,
  });

  const appetizeSession = useStore(appetizeSessionStore, (state) => state);
  const device = useMemo(() => {
    const data = appetizeSession?.getData?.();
    if (data) {
      return {
        width: data.device.screen.width,
        height: data.device.screen.height,
        offset: {
          x: data.device.embed.screen.offset.x,
          y: data.device.embed.screen.offset.y,
        },
      };
    }
  }, [appetizeSession]);

  if (!buildData.pubKey) {
    return (
      <p className="ml-6">
        {buildData.storage_path.endsWith(".ipa")
          ? "The .ipa builds are not supported in the web test editor"
          : "Incorrect configuration of the build file - missing public key"}
      </p>
    );
  }

  return (
    <>
      <AppetizeSimulator
        fromEditor={true}
        company={companyData}
        sessionConfig={sessionConfig}
        buildId={buildData.id}
        appetizePublicKey={buildData.pubKey}
        params={
          test?.settings?.launchParams?.reduce(
            (prev, current) => ({
              ...prev,
              [current.key]: current.value,
            }),
            {}
          ) ?? {}
        }
      />
      <AppetizeDeviceWrapper ref={wrapperRef} deviceSize={device}>
        <AnnotatedScreenshot
          setDeviceContextState={setDeviceContextState}
          deviceContextState={deviceContextState}
          usingGptDriverDeviceProvider={false}
        />
        {isTapping && (
          <div
            className="absolute w-8 h-8 pointer-events-none"
            style={{
              left: cursorPos.x - 16,
              top: cursorPos.y - 16,
            }}
          >
            <svg viewBox="0 0 32 32" className="w-full h-full">
              <circle
                cx="16"
                cy="16"
                r="8"
                className="fill-blue-500 opacity-30"
              />
              <circle
                cx="16"
                cy="16"
                r="6"
                className="fill-blue-500 opacity-60"
              />
            </svg>
          </div>
        )}
      </AppetizeDeviceWrapper>
    </>
  );
};

const AppetizeDeviceWrapper = styled.div<{
  deviceSize?: {
    width: number;
    height: number;
    offset: {
      x: number;
      y: number;
    };
  };
}>`
  --top: ${(props) => `${32 + (props.deviceSize?.offset?.y ?? 0)}px`};
  --bottom: ${(props) => `${32 + (props.deviceSize?.offset?.y ?? 0)}px`};
  position: absolute;
  top: var(--top);
  bottom: var(--bottom);
  margin-inline: auto;
  left: 50%;
  right: 50%;
  transform: translate(-50%, 0);

  ${(props) =>
    props.deviceSize != null &&
    `
    aspect-ratio: ${props.deviceSize.width / props.deviceSize.height};
    height: fill-available;
    height: calc(100% - var(--top) - var(--bottom));
  `}
`;

const TrailLineSvg = styled.svg<{
  device?: {
    width: number;
    height: number;
    offset: {
      x: number;
      y: number;
    };
  };
}>`
  position: absolute;
  z-index: 1;
  pointer-events: none;

  ${(props) =>
    props.device != null &&
    `
    aspect-ratio: ${props.device.width / props.device.height};
    height: -webkit-fill-available;
    height: 100%;
    left: 50%;
    right: 50%;
    transform: translate(-50%, 0);
  `}
`;

export default AppetizeDeviceProvider;
